import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchKnowledgeTimeline } from '../services/dashboardService';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler
);

// Deterministic colour from community title
const getCommunityColor = (title) => {
  if (!title) return '#6366f1';
  let hash = 0;
  for (let i = 0; i < title.length; i++) hash = title.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 60%, 50%)`;
};

const fmt = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

// ── Table ─────────────────────────────────────────────────────────────────

const TimelineTable = ({ entries, onSelectDoc }) => {
  const [sortKey, setSortKey] = useState('created_date');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...entries].sort((a, b) => {
    let av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
    if (sortKey === 'created_date') { av = new Date(av); bv = new Date(bv); }
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const SortIcon = ({ k }) => (
    <i className={`fas fa-sort${sortKey === k ? (sortDir === 'asc' ? '-up' : '-down') : ''} ktc-sort-icon`}></i>
  );

  return (
    <div className="ktc-table-wrap">
      <table className="ktc-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('file_name')} className="ktc-sortable">
              File Name <SortIcon k="file_name" />
            </th>
            <th onClick={() => handleSort('created_date')} className="ktc-sortable">
              Date <SortIcon k="created_date" />
            </th>
            <th onClick={() => handleSort('nodes_contributed')} className="ktc-sortable">
              Nodes Added <SortIcon k="nodes_contributed" />
            </th>
            <th onClick={() => handleSort('edges_contributed')} className="ktc-sortable">
              Edges Added <SortIcon k="edges_contributed" />
            </th>
            <th onClick={() => handleSort('cumulative_edges')} className="ktc-sortable">
              Cumulative Edges <SortIcon k="cumulative_edges" />
            </th>
            <th>Dominant Topic</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((entry, i) => (
            <tr
              key={entry.pdf_id ?? i}
              className="ktc-table-row"
              onClick={() => onSelectDoc && onSelectDoc(entry)}
            >
              <td className="ktc-file-name">{entry.file_name || `PDF #${entry.pdf_id}`}</td>
              <td>{fmt(entry.created_date)}</td>
              <td>{entry.nodes_contributed ?? 0}</td>
              <td>{entry.edges_contributed ?? 0}</td>
              <td><strong>{entry.cumulative_edges ?? 0}</strong></td>
              <td>
                {entry.dominant_community ? (
                  <span
                    className="ktc-dominant-chip"
                    style={{ background: getCommunityColor(entry.dominant_community) }}
                    title={entry.dominant_community}
                  >
                    {entry.dominant_community.length > 22
                      ? entry.dominant_community.slice(0, 22) + '…'
                      : entry.dominant_community}
                  </span>
                ) : (
                  <span className="ktc-no-topic">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Tooltip content builder ────────────────────────────────────────────────

const buildChartData = (entries) => {
  const sorted = [...entries].sort(
    (a, b) => new Date(a.created_date) - new Date(b.created_date)
  );

  const maxEdges = Math.max(...sorted.map(e => e.edges_contributed ?? 0), 1);

  // Find major contributors (>30% of total growth)
  const totalEdges = sorted[sorted.length - 1]?.cumulative_edges ?? 0;

  const pointColors = sorted.map(e =>
    getCommunityColor(e.dominant_community || '')
  );

  const pointRadius = sorted.map(e =>
    (e.edges_contributed ?? 0) / maxEdges > 0.3 ? 10 : 5
  );

  return {
    sorted,
    chartData: {
      labels: sorted.map(e => fmt(e.created_date)),
      datasets: [
        {
          label: 'Cumulative Relationships',
          data: sorted.map(e => e.cumulative_edges ?? 0),
          borderColor: 'var(--accent-primary, #6366f1)',
          backgroundColor: 'rgba(99,102,241,0.12)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: pointColors,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius,
          pointHoverRadius: 10,
        },
      ],
    },
    totalEdges,
  };
};

const chartOptions = (sorted) => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(128,128,128,0.12)' },
      ticks: { color: 'var(--text-secondary, #888)' },
      title: {
        display: true,
        text: 'Cumulative Relationships',
        color: 'var(--text-secondary, #888)',
      },
    },
    x: {
      grid: { display: false },
      ticks: { color: 'var(--text-secondary, #888)', maxRotation: 45 },
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title: (ctx) => {
          const entry = sorted[ctx[0].dataIndex];
          return entry?.file_name || `Document ${ctx[0].dataIndex + 1}`;
        },
        afterTitle: (ctx) => {
          const entry = sorted[ctx[0].dataIndex];
          return `Date: ${fmt(entry?.created_date)}`;
        },
        label: (ctx) => {
          const entry = sorted[ctx.dataIndex];
          return [
            `Cumulative: ${ctx.raw} relationships`,
            `Added: ${entry?.edges_contributed ?? 0} relationships, ${entry?.nodes_contributed ?? 0} entities`,
            entry?.dominant_community ? `Topic: ${entry.dominant_community}` : '',
          ].filter(Boolean);
        },
      },
    },
  },
});

// ── Main KnowledgeTimelineChart ────────────────────────────────────────────

const KnowledgeTimelineChart = ({ onSelectDoc }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const chartRef = useRef(null);

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchKnowledgeTimeline();
      if (res.success) {
        setEntries(res.data || []);
      } else {
        setError(res.message || 'Failed to load timeline.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  if (loading) {
    return (
      <div className="ktc-container">
        <div className="ktc-loading">
          <div className="ktc-spinner"></div>
          <p>Loading timeline…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ktc-container">
        <div className="ktc-error">
          <i className="fas fa-exclamation-triangle"></i> {error}
          <button className="btn-secondary" onClick={loadTimeline} style={{ marginLeft: 12 }}>Retry</button>
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="ktc-container">
        <div className="ktc-empty">
          <i className="fas fa-chart-line ktc-empty-icon"></i>
          <h3>No timeline data available</h3>
          <p>Process PDF documents to see how your knowledge graph grew over time.</p>
        </div>
      </div>
    );
  }

  const { sorted, chartData, totalEdges } = buildChartData(entries);
  const maxEdges = Math.max(...sorted.map(e => e.edges_contributed ?? 0), 1);

  // Annotate major contributor
  const majorContributor = sorted.reduce((best, e) =>
    (e.edges_contributed ?? 0) > (best.edges_contributed ?? 0) ? e : best, sorted[0]
  );
  const isMajor = (majorContributor.edges_contributed ?? 0) / totalEdges > 0.3;

  return (
    <div className="ktc-container">
      {/* Header */}
      <div className="ktc-toolbar">
        <h3><i className="fas fa-chart-line"></i> Knowledge Timeline</h3>
        <div className="ktc-actions">
          <button
            className={`ktc-view-btn ${showTable ? '' : 'active'}`}
            onClick={() => setShowTable(false)}
            title="Chart view"
          >
            <i className="fas fa-chart-area"></i>
          </button>
          <button
            className={`ktc-view-btn ${showTable ? 'active' : ''}`}
            onClick={() => setShowTable(true)}
            title="Table view"
          >
            <i className="fas fa-table"></i>
          </button>
        </div>
      </div>

      {/* Stats summary */}
      <div className="ktc-summary">
        <div className="ktc-stat">
          <span className="ktc-stat-value">{totalEdges}</span>
          <span className="ktc-stat-label">Total Relationships</span>
        </div>
        <div className="ktc-stat">
          <span className="ktc-stat-value">{sorted.length}</span>
          <span className="ktc-stat-label">Documents Processed</span>
        </div>
        {isMajor && (
          <div className="ktc-major-notice">
            <i className="fas fa-star"></i> Major contribution:{' '}
            <strong>{majorContributor.file_name}</strong> added{' '}
            {((majorContributor.edges_contributed / totalEdges) * 100).toFixed(0)}% of total relationships
          </div>
        )}
      </div>

      {!showTable ? (
        <div className="ktc-chart-wrap">
          <Line
            ref={chartRef}
            data={chartData}
            options={chartOptions(sorted)}
          />
        </div>
      ) : (
        <TimelineTable entries={sorted} onSelectDoc={onSelectDoc} />
      )}
    </div>
  );
};

export default KnowledgeTimelineChart;
